"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";

/**
 * 2026-05-04 redesign — collapsed the cluttered 4-tab strip
 * (Trades / Posts / Comments / Activity) to 2 (Posts + Activity).
 *
 * Founder direction with screenshot 2026-05-04: the IG/FB pattern is
 * one unified feed with content-type filters as sub-tabs, not
 * top-level tabs. Trades now surface inside `/feed/posts` via the
 * `kind="trade"` PostRecord type; comments surface inline on the
 * post they belong to + as activity highlights, no longer need a
 * standalone /feed/comments view in the top strip.
 *
 * The /feed (markets) and /feed/comments routes are preserved for
 * existing bookmarks + deep-links — they just no longer appear in
 * the top strip.
 *
 *   /feed/posts     — unified content feed (posts + trade-as-post +
 *                     premium gating)
 *   /feed/activity  — chronological union (likes / comments / copy-
 *                     subs / market resolutions); authed-only
 */
export function FeedTabs({
  active,
}: {
  // 'trades' + 'comments' kept in the union temporarily so the legacy
  // /feed and /feed/comments pages can still pass a value through —
  // they render no highlighted tab in the new strip (intentional).
  active: "posts" | "activity" | "trades" | "comments";
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t("feedTabs.ariaLabel")}
      className="mb-3 flex gap-2 overflow-x-auto border-b border-[var(--color-border)]"
    >
      <FeedTab
        href="/feed/posts"
        label={t("feedTabs.posts")}
        active={active === "posts"}
      />
      <FeedTab
        href="/feed/activity"
        label={t("feedTabs.activity", "Activity")}
        active={active === "activity"}
      />
    </nav>
  );
}

function FeedTab({
  href,
  label,
  active,
}: {
  href: string;
  label: string;
  active: boolean;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.4px] transition-colors"
      style={{
        color: active ? "var(--color-brand)" : "var(--color-text3)",
        borderBottom: active
          ? "2px solid var(--color-brand)"
          : "2px solid transparent",
      }}
    >
      {label}
    </Link>
  );
}
